import React, { useEffect } from "react";
import dynamic from "next/dynamic";

// Hero is critical for LCP - must be SSR
import Hero from "@/app/main/components/sections/hero";
// SEO/GEO content - must be SSR so AI crawlers and search engines can read it
import SeoIntro from "@/app/main/components/sections/seoIntro";

// Lenis smooth scroll - defer loading
const ReactLenis = dynamic(
  () => import("lenis/react").then((mod) => mod.ReactLenis),
  { ssr: false }
);

// Below-the-fold sections - lazy load with SSR disabled
const AboutUs = dynamic(
  () => import("@/app/main/components/sections/aboutUs"),
  { ssr: false }
);
const Benefits = dynamic(
  () => import("@/app/main/components/sections/benefits"),
  { ssr: false }
);
const Teachers = dynamic(
  () => import("@/app/main/components/sections/teachers"),
  { ssr: false }
);
const OnlineLesson = dynamic(
  () => import("@/app/main/components/sections/onlineLesson"),
  { ssr: false }
);
const Prices = dynamic(() => import("@/app/main/components/sections/prices"), {
  ssr: false,
});
const Results = dynamic(
  () => import("@/app/main/components/sections/results"),
  { ssr: false }
);
const Certificates = dynamic(
  () => import("@/app/main/components/sections/certificates"),
  { ssr: false }
);
const Events = dynamic(() => import("@/app/main/components/sections/events"), {
  ssr: false,
});
const Branches = dynamic(
  () => import("@/app/main/components/sections/branches"),
  { ssr: false }
);
const Locations = dynamic(
  () => import("@/app/main/components/sections/locations"),
  { ssr: false }
);
const FAQ = dynamic(() => import("@/app/main/components/sections/faq"), {
  ssr: false,
});

// Modal components - only load when needed
const LeadFormModal = dynamic(
  () => import("@/app/main/components/leadFormModal"),
  { ssr: false }
);
const LeadFormDrawer = dynamic(
  () => import("@/app/main/components/leadFormDrawer"),
  { ssr: false }
);
const ModalVideo = dynamic(() => import("@/app/main/components/modalVideo"), {
  ssr: false,
});
const EventVideos = dynamic(
  () =>
    import(
      "@/app/main/components/sections/benefits/components/events/eventVideos"
    ),
  { ssr: false }
);
const ResultView = dynamic(() => import("@/app/main/components/resultView"), {
  ssr: false,
});

import { useGetHomePageData } from "@/hooks/usePageData";
import { Wrapper } from "@/app/main/style";
import { useAppSelector } from "@/store/hook";

// Wrapper component for smooth scroll - renders children immediately, applies Lenis when loaded
const SmoothScrollWrapper = ({ children }: { children: React.ReactNode }) => {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <>{children}</>;
  }

  return <ReactLenis root>{children}</ReactLenis>;
};

export default function MainPage({ hideMain }: { hideMain?: boolean }) {
  const { data } = useGetHomePageData({});
  const { leadForm, leadFormDrawer, eventVideos, video, resultView } =
    useAppSelector((state) => state.modals);

  const sections = data?.sections;

  return (
    <SmoothScrollWrapper>
      <Wrapper>
        {!hideMain && <Hero data={sections?.hero} />}

        {!hideMain && <SeoIntro />}

        <AboutUs data={sections?.aboutUs} company={data?.company} />

        <Benefits data={sections?.benefits} />

        <Teachers data={sections?.teachers} />

        <OnlineLesson data={sections?.online} />

        <Prices data={sections?.prices} />

        <Results data={sections?.results} />

        <Certificates data={sections?.certificate} />

        <Events data={sections?.events} />

        <Branches data={sections?.branches} />

        <Locations data={sections?.branches} />

        <FAQ data={sections?.faq} />

        {leadForm.open && <LeadFormModal />}
        {leadFormDrawer.open && <LeadFormDrawer />}
        {video.open && <ModalVideo />}
        {eventVideos.open && <EventVideos />}
        {resultView.open && <ResultView />}
      </Wrapper>
    </SmoothScrollWrapper>
  );
}
